/**
 * Thin typed fetch wrapper for Tiqora REST /api/v1.
 *
 * - credentials: 'include' for session cookies
 * - normalises errors to ApiError
 * - optional 401 → login redirect handler
 */
export class ApiError extends Error {
    status;
    detail;
    path;
    /** Seconds from a `Retry-After` header (429/503), when the server sent one. */
    retryAfterSeconds;
    constructor(status, detail, path, retryAfterSeconds) {
        const message = typeof detail === "string"
            ? detail
            : detail && typeof detail === "object" && "detail" in detail
                ? String(detail.detail)
                : `HTTP ${status}`;
        super(message);
        this.name = "ApiError";
        this.status = status;
        this.detail = detail;
        this.path = path;
        this.retryAfterSeconds = retryAfterSeconds;
    }
    get isUnauthorized() {
        return this.status === 401;
    }
    get isForbidden() {
        return this.status === 403;
    }
    get isNotFound() {
        return this.status === 404;
    }
}
function joinUrl(base, path) {
    if (path.startsWith("http://") || path.startsWith("https://"))
        return path;
    const b = base.replace(/\/$/, "");
    const p = path.startsWith("/") ? path : `/${path}`;
    return `${b}${p}`;
}
export class ApiClient {
    baseUrl;
    onUnauthorized;
    skipAuthRedirectPaths;
    fetchImpl;
    constructor(options = {}) {
        this.baseUrl = options.baseUrl ?? "";
        this.onUnauthorized = options.onUnauthorized;
        this.skipAuthRedirectPaths = options.skipAuthRedirectPaths ?? [
            "/api/v1/auth/login",
            "/api/v1/auth/me",
            // Pending-2FA / forced-enroll ceremonies must not bounce to /login on 401.
            "/api/v1/auth/passkey/authenticate",
            "/api/v1/auth/passkey/register",
            "/api/v1/auth/totp/verify",
            "/api/v1/auth/totp/enroll",
            "/api/v1/auth/totp/confirm",
            "/api/portal/auth/login",
            "/api/portal/auth/me",
        ];
        this.fetchImpl = options.fetch ?? globalThis.fetch.bind(globalThis);
    }
    async request(method, path, init) {
        return (await this.requestWithHeaders(method, path, init)).data;
    }
    /** Like `request`, plus the response headers (for endpoints that answer metadata in a header). */
    async requestWithHeaders(method, path, init) {
        let url = joinUrl(this.baseUrl, path);
        if (init?.query) {
            const qs = new URLSearchParams();
            for (const [k, v] of Object.entries(init.query)) {
                if (v === undefined || v === null || v === "")
                    continue;
                if (Array.isArray(v)) {
                    for (const item of v) {
                        if (item === undefined || item === null || item === "")
                            continue;
                        qs.append(k, String(item));
                    }
                    continue;
                }
                qs.set(k, String(v));
            }
            const s = qs.toString();
            if (s)
                url += (url.includes("?") ? "&" : "?") + s;
        }
        const headers = {
            Accept: "application/json",
            ...init?.headers,
        };
        let body;
        if (init?.body instanceof FormData) {
            body = init.body;
        }
        else if (init?.body !== undefined) {
            headers["Content-Type"] = "application/json";
            body = JSON.stringify(init.body);
        }
        const res = await this.fetchImpl(url, {
            method,
            headers,
            body,
            credentials: "include",
            signal: init?.signal,
        });
        if (res.status === 204) {
            return { data: undefined, headers: res.headers };
        }
        const contentType = res.headers.get("content-type") ?? "";
        const isJson = contentType.includes("application/json");
        const payload = isJson
            ? await res.json().catch(() => null)
            : await res.text().catch(() => null);
        if (!res.ok) {
            if (res.status === 401 &&
                this.onUnauthorized &&
                !this.skipAuthRedirectPaths.some((p) => path.startsWith(p))) {
                this.onUnauthorized();
            }
            const retryAfter = Number(res.headers.get("retry-after"));
            throw new ApiError(res.status, payload ?? res.statusText, path, Number.isFinite(retryAfter) && retryAfter > 0 ? Math.ceil(retryAfter) : undefined);
        }
        return { data: payload, headers: res.headers };
    }
    // ── Auth ──────────────────────────────────────────────────────────────
    login(body, signal) {
        return this.request("POST", "/api/v1/auth/login", {
            body,
            signal,
        });
    }
    me(signal) {
        return this.request("GET", "/api/v1/auth/me", { signal });
    }
    /** Persist Znuny-compatible UserLanguage (also drives notification templates). */
    setMyLanguage(language, signal) {
        return this.request("PUT", "/api/v1/auth/me/language", {
            body: { language },
            signal,
        });
    }
    /** Persist Znuny-compatible UserTimeZone (IANA name); null clears it. */
    setMyTimeZone(timeZone, signal) {
        return this.request("PUT", "/api/v1/auth/me/time-zone", {
            body: { time_zone: timeZone },
            signal,
        });
    }
    logout(signal) {
        return this.request("POST", "/api/v1/auth/logout", { signal });
    }
    authMethods(signal) {
        return this.request("GET", "/api/v1/auth/methods", { signal });
    }
    totpVerify(body, signal) {
        return this.request("POST", "/api/v1/auth/totp/verify", {
            body,
            signal,
        });
    }
    totpEnroll(signal) {
        return this.request("POST", "/api/v1/auth/totp/enroll", { signal });
    }
    totpConfirm(body, signal) {
        return this.request("POST", "/api/v1/auth/totp/confirm", {
            body,
            signal,
        });
    }
    totpDisable(body, signal) {
        return this.request("DELETE", "/api/v1/auth/totp", {
            body,
            signal,
        });
    }
    totpStatus(signal) {
        return this.request("GET", "/api/v1/auth/totp/status", { signal });
    }
    // ── Passkeys (WebAuthn) ─────────────────────────────────────────────
    passkeyRegisterBegin(signal) {
        return this.request("POST", "/api/v1/auth/passkey/register/begin", {
            signal,
        });
    }
    passkeyRegisterFinish(body, signal) {
        return this.request("POST", "/api/v1/auth/passkey/register/finish", {
            body,
            signal,
        });
    }
    passkeyAuthenticateBegin(signal) {
        return this.request("POST", "/api/v1/auth/passkey/authenticate/begin", { signal });
    }
    passkeyAuthenticateFinish(body, signal) {
        return this.request("POST", "/api/v1/auth/passkey/authenticate/finish", {
            body,
            signal,
        });
    }
    passkeyList(signal) {
        return this.request("GET", "/api/v1/auth/passkey", { signal });
    }
    passkeyDelete(passkeyId, signal) {
        return this.request("DELETE", `/api/v1/auth/passkey/${passkeyId}`, { signal });
    }
    /** Browser-navigates to the OIDC provider; not a fetch (redirect flow). */
    oidcLoginUrl() {
        return "/api/v1/auth/oidc/login";
    }
    // ── Queues ────────────────────────────────────────────────────────────
    listQueues(signal) {
        return this.request("GET", "/api/v1/queues", { signal });
    }
    // ── Tickets ───────────────────────────────────────────────────────────
    listTickets(params = {}, signal) {
        return this.request("GET", "/api/v1/tickets", {
            query: params,
            signal,
        });
    }
    /**
     * Inbox segment + chip counts for the given list filters, in one request.
     * `states` ignores `state_type`/`state_id` (one count per segment, `all` =
     * no state filter); `flags` applies the state filter but ignores
     * `escalated`/`locked`/`unassigned` (each = the rest plus only that flag).
     */
    ticketFacets(params = {}, signal) {
        return this.request("GET", "/api/v1/tickets/facets", {
            query: params,
            signal,
        });
    }
    /** Open/new counts for tickets owned by the current agent (nav badges). */
    myTicketCounts(signal) {
        return this.request("GET", "/api/v1/tickets/my-counts", {
            signal,
        });
    }
    /** KPI-tile counts for the agent dashboard: owned open/new, unclaimed new, SLA-escalated, AI handoff. */
    dashboardSummary(signal) {
        return this.request("GET", "/api/v1/tickets/dashboard-summary", {
            signal,
        });
    }
    /**
     * Permission-scoped ticket search for link/merge pickers.
     * Matches tn + title; only queues the agent has at least `ro` on.
     */
    searchTickets(params, signal) {
        return this.request("GET", "/api/v1/tickets/search", {
            query: params,
            signal,
        });
    }
    getTicket(ticketId, signal) {
        return this.request("GET", `/api/v1/tickets/${ticketId}`, { signal });
    }
    /** Closed tickets similar to *ticketId* (Meili keyword rank v1). */
    getSimilarTickets(ticketId, signal) {
        return this.request("GET", `/api/v1/tickets/${ticketId}/similar`, { signal });
    }
    listArticles(ticketId, signal) {
        return this.request("GET", `/api/v1/tickets/${ticketId}/articles`, { signal });
    }
    getArticleBody(ticketId, articleId, signal) {
        return this.request("GET", `/api/v1/tickets/${ticketId}/articles/${articleId}/body`, { signal });
    }
    listAttachments(ticketId, articleId, signal) {
        return this.request("GET", `/api/v1/tickets/${ticketId}/articles/${articleId}/attachments`, { signal });
    }
    /** An HTML attachment (e.g. PGPexch.htm) sanitised like an article body. */
    getAttachmentHtml(ticketId, articleId, attachmentId, signal) {
        return this.request("GET", `/api/v1/tickets/${ticketId}/articles/${articleId}/attachments/${attachmentId}/html`, { signal });
    }
    /** Parsed PGP public key(s) in an attachment — read-only, nothing imported. */
    getAttachmentPgpKey(ticketId, articleId, attachmentId, signal) {
        return this.request("GET", `/api/v1/tickets/${ticketId}/articles/${articleId}/attachments/${attachmentId}/pgp-key`, { signal });
    }
    /** Import the attached public key into the keyring (rw in admin or users). */
    importAttachmentPgpKey(ticketId, articleId, attachmentId) {
        return this.request("POST", `/api/v1/tickets/${ticketId}/articles/${articleId}/attachments/${attachmentId}/pgp-key/import`);
    }
    attachmentDownloadUrl(ticketId, articleId, attachmentId, download = true) {
        const q = download ? "?download=true" : "";
        return joinUrl(this.baseUrl, `/api/v1/tickets/${ticketId}/articles/${articleId}/attachments/${attachmentId}${q}`);
    }
    /** Download URL of one customer's vCard (session-cookie auth, like attachments). */
    customerVcardUrl(login) {
        return joinUrl(this.baseUrl, `/api/v1/customers/${encodeURIComponent(login)}/vcard`);
    }
    /** Download URL of one .vcf holding all contacts of a company. */
    companyVcardsUrl(customerId) {
        return joinUrl(this.baseUrl, `/api/v1/customers/companies/${encodeURIComponent(customerId)}/vcards`);
    }
    /** Customer directory page (needs the customer_directory feature). */
    listCustomerDirectory(params, signal) {
        return this.request("GET", "/api/v1/customer-directory", {
            query: {
                page: params.page,
                page_size: params.pageSize,
                valid: params.valid,
                search: params.search || undefined,
                customer_id: params.customerId || undefined,
                sort: params.sort,
                order: params.sort ? params.order : undefined,
            },
            signal,
        });
    }
    /** The agent's favorites plus own recent and frequent customer users (last 90 days of articles). */
    getCustomerShortlist(signal) {
        return this.request("GET", "/api/v1/customer-directory/shortlist", {
            signal,
        });
    }
    /** Star (true) or unstar (false) a customer user for the current agent. */
    setCustomerFavorite(login, favorite) {
        return this.request(favorite ? "PUT" : "DELETE", `/api/v1/customer-directory/favorites/${encodeURIComponent(login)}`);
    }
    /** One company's master data and valid contact count. */
    getCustomerDirectoryCompany(customerId, signal) {
        return this.request("GET", `/api/v1/customer-directory/companies/${encodeURIComponent(customerId)}`, { signal });
    }
    searchCustomerDirectoryCompanies(search, signal) {
        return this.request("GET", "/api/v1/customer-directory/companies", { query: { search: search || undefined }, signal });
    }
    /**
     * Download URL of one .vcf with everything matching the filter (the server
     * caps the count). Session-cookie auth, like attachments.
     */
    customerDirectoryVcardsUrl(filter) {
        const q = new URLSearchParams();
        if (filter.search)
            q.set("search", filter.search);
        if (filter.customerId)
            q.set("customer_id", filter.customerId);
        if (filter.valid)
            q.set("valid", filter.valid);
        const qs = q.toString();
        return joinUrl(this.baseUrl, `/api/v1/customer-directory/vcards${qs ? `?${qs}` : ""}`);
    }
    /** vCard text (.vcf) of exactly these contacts; POST because selections get long. */
    exportCustomerVcards(logins, signal) {
        return this.request("POST", "/api/v1/customer-directory/vcards", {
            body: { logins },
            signal,
        });
    }
    listHistory(ticketId, order = "desc", signal) {
        return this.request("GET", `/api/v1/tickets/${ticketId}/history?order=${order}`, { signal });
    }
    createArticle(ticketId, body, signal) {
        return this.request("POST", `/api/v1/tickets/${ticketId}/articles`, { body, signal });
    }
    getReplyDraft(ticketId, articleId, replyAll = false, signal) {
        return this.request("GET", `/api/v1/tickets/${ticketId}/articles/${articleId}/reply-draft?reply_all=${replyAll}`, { signal });
    }
    getArticleAiOrigin(ticketId, articleId, signal) {
        return this.request("GET", `/api/v1/tickets/${ticketId}/articles/${articleId}/ai-origin`, { signal });
    }
    listTemplates(ticketId, type, signal) {
        const q = type ? `?type=${type}` : "";
        return this.request("GET", `/api/v1/tickets/${ticketId}/templates${q}`, { signal });
    }
    getTelegramChat(ticketId, signal) {
        return this.request("GET", `/api/v1/tickets/${ticketId}/telegram`, { signal });
    }
    postTelegramTyping(ticketId, signal) {
        return this.request("POST", `/api/v1/tickets/${ticketId}/telegram/typing`, { signal });
    }
    editTelegramMessage(ticketId, articleId, body, signal) {
        return this.request("PATCH", `/api/v1/tickets/${ticketId}/articles/${articleId}/telegram`, { body: { body }, signal });
    }
    retractTelegramMessage(ticketId, articleId, signal) {
        return this.request("POST", `/api/v1/tickets/${ticketId}/articles/${articleId}/telegram/retract`, { signal });
    }
    removeTelegramButtons(ticketId, articleId, signal) {
        return this.request("DELETE", `/api/v1/tickets/${ticketId}/articles/${articleId}/telegram/buttons`, { signal });
    }
    patchTicket(ticketId, body, signal) {
        return this.request("PATCH", `/api/v1/tickets/${ticketId}`, {
            body,
            signal,
        });
    }
    acquireTicketLock(ticketId, body, signal) {
        return this.request("POST", `/api/v1/tickets/${ticketId}/acquire-lock`, { body, signal });
    }
    mergeTicket(ticketId, body, signal) {
        return this.request("POST", `/api/v1/tickets/${ticketId}/merge`, {
            body,
            signal,
        });
    }
    createTicket(body, signal) {
        return this.request("POST", "/api/v1/tickets", {
            body,
            signal,
        });
    }
    /** PGP / S/MIME compose options (sign keys, recipient keys) for a reply or
     * forward on `ticketId`; `ticketId = null` + `queue_id` for a new ticket. */
    getCryptoOptions(ticketId, params, signal) {
        const path = ticketId == null
            ? "/api/v1/tickets/crypto-options"
            : `/api/v1/tickets/${ticketId}/crypto-options`;
        return this.request("GET", path, { query: params, signal });
    }
    forwardArticle(ticketId, articleId, body, signal) {
        return this.request("POST", `/api/v1/tickets/${ticketId}/articles/${articleId}/forward`, { body, signal });
    }
    bounceArticle(ticketId, articleId, body, signal) {
        return this.request("POST", `/api/v1/tickets/${ticketId}/articles/${articleId}/bounce`, { body, signal });
    }
    splitArticle(ticketId, articleId, body, signal) {
        return this.request("POST", `/api/v1/tickets/${ticketId}/articles/${articleId}/split`, { body, signal });
    }
    /** Hard-delete an internal note. Requires `rw`; 409 if the article isn't an
     * internal, non-customer-visible note. */
    deleteArticle(ticketId, articleId, signal) {
        return this.request("DELETE", `/api/v1/tickets/${ticketId}/articles/${articleId}`, { signal });
    }
    listTicketLinks(ticketId, signal) {
        return this.request("GET", `/api/v1/tickets/${ticketId}/links`, { signal });
    }
    createTicketLink(ticketId, body, signal) {
        return this.request("POST", `/api/v1/tickets/${ticketId}/links`, {
            body,
            signal,
        });
    }
    postPresence(ticketId, body, signal) {
        return this.request("POST", `/api/v1/tickets/${ticketId}/presence`, {
            body,
            signal,
        });
    }
    getPresence(ticketId, signal) {
        return this.request("GET", `/api/v1/tickets/${ticketId}/presence`, { signal });
    }
    /** Currently-online agents (Redis TTL presence). */
    getOnlineAgents(signal) {
        return this.request("GET", "/api/v1/agents/online", {
            signal,
        });
    }
    /** Lightweight global presence heartbeat (idle-but-open sessions). */
    pingOnlinePresence(signal) {
        return this.request("POST", "/api/v1/agents/presence/ping", {
            signal,
        });
    }
    /**
     * Build the CSV export URL for the current ticket-list filters. Consumed
     * via a plain navigation/anchor (cookie-authenticated download), not a
     * fetch call — mirrors {@link ApiClient.eventStreamUrl}.
     */
    exportTicketsCsvUrl(params = {}) {
        const qs = new URLSearchParams();
        for (const [k, v] of Object.entries(params)) {
            if (v === undefined || v === null || v === "")
                continue;
            if (Array.isArray(v)) {
                for (const item of v)
                    qs.append(k, String(item));
                continue;
            }
            qs.set(k, String(v));
        }
        const suffix = qs.toString();
        return joinUrl(this.baseUrl, `/api/v1/tickets/export.csv${suffix ? `?${suffix}` : ""}`);
    }
    // ── Realtime events (SSE) ────────────────────────────────────────────────
    // Consumed via the browser EventSource API directly, not a fetch call —
    // this just builds the URL against the configured base URL/credentials.
    eventStreamUrl() {
        return joinUrl(this.baseUrl, "/api/v1/events/stream");
    }
    // ── Customers ─────────────────────────────────────────────────────────
    getCustomer(login, signal) {
        return this.request("GET", `/api/v1/customers/${encodeURIComponent(login)}`, { signal });
    }
    /** A customer's PGP keys / S-MIME certificates (Znuny customer preferences PGP/SMIME). */
    get customerCryptoKeys() {
        const enc = encodeURIComponent;
        const base = (login) => `/api/v1/customers/${enc(login)}/crypto-keys`;
        return {
            list: (login, signal) => this.request("GET", base(login), { signal }),
            uploadPgp: (login, asciiArmor, signal) => this.request("POST", `${base(login)}/pgp`, {
                body: { ascii_armor: asciiArmor },
                signal,
            }),
            /** PEM text, or base64 of a DER / PKCS#7 / PKCS#12 file. */
            uploadSmime: (login, certificate, signal) => this.request("POST", `${base(login)}/smime`, {
                body: { certificate },
                signal,
            }),
            deletePgp: (login, keyId, signal) => this.request("DELETE", `${base(login)}/pgp/${enc(keyId)}`, {
                signal,
            }),
            deleteSmime: (login, filename, signal) => this.request("DELETE", `${base(login)}/smime/${enc(filename)}`, {
                signal,
            }),
        };
    }
    /** Overwrite a customer's contact data (needs the customer_edit feature). */
    updateCustomer(login, body, signal) {
        return this.request("PUT", `/api/v1/customers/${encodeURIComponent(login)}`, { body, signal });
    }
    /**
     * Create a customer_user as any authenticated agent (ticket "Kunde anlegen").
     * No password — contact record only; portal auth is separate.
     */
    createCustomer(body, signal) {
        return this.request("POST", "/api/v1/customers", {
            body,
            signal,
        });
    }
    // ── Reference (agent pickers, /api/v1/reference) ─────────────────────────
    listReferencePriorities(signal) {
        return this.request("GET", "/api/v1/reference/priorities", { signal });
    }
    listReferenceTypes(signal) {
        return this.request("GET", "/api/v1/reference/types", { signal });
    }
    listReferenceServices(signal) {
        return this.request("GET", "/api/v1/reference/services", { signal });
    }
    listReferenceSlas(params = {}, signal) {
        return this.request("GET", "/api/v1/reference/slas", {
            query: params,
            signal,
        });
    }
    listReferenceStates(signal) {
        return this.request("GET", "/api/v1/reference/states", { signal });
    }
    listReferenceAgents(signal) {
        return this.request("GET", "/api/v1/reference/agents", { signal });
    }
    /** Valid roles (id, name) for approver pickers; readable by every agent. */
    listReferenceRoles(signal) {
        return this.request("GET", "/api/v1/reference/roles", { signal });
    }
    searchReferenceCustomers(params = {}, signal) {
        return this.request("GET", "/api/v1/reference/customers", {
            query: params,
            signal,
        });
    }
    /**
     * Quick company + contact search for agent typeaheads.
     * Distinct from ``searchReferenceCustomers`` (contacts only).
     */
    customerQuickSearch(params = {}, signal) {
        return this.request("GET", "/api/v1/reference/customer-search", {
            query: params,
            signal,
        });
    }
    /**
     * Queues for agent pickers. Pass `movable: true` for the Verschieben
     * (move) picker — only valid queues the agent has `rw` on.
     */
    listReferenceQueues(params = {}, signal) {
        return this.request("GET", "/api/v1/reference/queues", {
            query: params,
            signal,
        });
    }
    /** From-address + signature preview + rich-text flag for the new-ticket compose form. */
    getComposeContext(queueId, signal) {
        return this.request("GET", "/api/v1/reference/compose-context", {
            query: { queue_id: queueId },
            signal,
        });
    }
    // ── Search ────────────────────────────────────────────────────────────
    search(params, signal) {
        return this.request("GET", "/api/v1/search", {
            query: params,
            signal,
        });
    }
    // ── Knowledge base (agent, /api/v1/kb) ──────────────────────────────────
    listKbCategories(signal) {
        return this.request("GET", "/api/v1/kb/categories", { signal });
    }
    createKbCategory(body, signal) {
        return this.request("POST", "/api/v1/kb/categories", { body, signal });
    }
    updateKbCategory(categoryId, body, signal) {
        return this.request("PATCH", `/api/v1/kb/categories/${categoryId}`, {
            body,
            signal,
        });
    }
    deleteKbCategory(categoryId, signal) {
        return this.request("DELETE", `/api/v1/kb/categories/${categoryId}`, { signal });
    }
    listAssignableGroups(signal) {
        return this.request("GET", "/api/v1/kb/assignable-groups", { signal });
    }
    listKbArticles(params = {}, signal) {
        return this.request("GET", "/api/v1/kb/articles", {
            query: params,
            signal,
        });
    }
    /** All KB tag names with ACL-filtered visible-article counts. */
    listKbTags(signal) {
        return this.request("GET", "/api/v1/kb/tags", { signal });
    }
    /** ACL-filtered agent-knowledge bundle selected by tag(s) and/or category. */
    getKbKnowledge(params = {}, signal) {
        return this.request("GET", "/api/v1/kb/knowledge", {
            query: params,
            signal,
        });
    }
    createKbArticle(body, signal) {
        return this.request("POST", "/api/v1/kb/articles", { body, signal });
    }
    getKbArticle(articleId, signal) {
        return this.request("GET", `/api/v1/kb/articles/${articleId}`, { signal });
    }
    updateKbArticle(articleId, body, signal) {
        return this.request("PATCH", `/api/v1/kb/articles/${articleId}`, {
            body,
            signal,
        });
    }
    deleteKbArticle(articleId, signal) {
        return this.request("DELETE", `/api/v1/kb/articles/${articleId}`, { signal });
    }
    publishKbArticle(articleId, signal) {
        return this.request("POST", `/api/v1/kb/articles/${articleId}/publish`, {
            signal,
        });
    }
    listKbArticleVersions(articleId, signal) {
        return this.request("GET", `/api/v1/kb/articles/${articleId}/versions`, { signal });
    }
    // KB article attachments (multipart field name `file`).
    listKbAttachments(articleId, signal) {
        return this.request("GET", `/api/v1/kb/articles/${articleId}/attachments`, { signal });
    }
    uploadKbAttachment(articleId, file, signal) {
        const form = new FormData();
        form.append("file", file);
        return this.request("POST", `/api/v1/kb/articles/${articleId}/attachments`, { body: form, signal });
    }
    kbAttachmentDownloadUrl(articleId, attachmentId) {
        return joinUrl(this.baseUrl, `/api/v1/kb/articles/${articleId}/attachments/${attachmentId}`);
    }
    deleteKbAttachment(articleId, attachmentId, signal) {
        return this.request("DELETE", `/api/v1/kb/articles/${articleId}/attachments/${attachmentId}`, { signal });
    }
    searchKb(params, signal) {
        return this.request("GET", "/api/v1/kb/search", {
            query: params,
            signal,
        });
    }
    // ── Admin ────────────────────────────────────────────────────────────────
    adminCrud(base) {
        return {
            list: (params, signal) => this.request("GET", base, {
                query: {
                    page: params?.page,
                    page_size: params?.pageSize,
                    valid: params?.valid,
                    search: params?.search,
                    regex: params?.regex,
                    sort: params?.sort,
                    order: params?.order,
                },
                signal,
            }),
            create: (body, signal) => this.request("POST", base, { body, signal }),
            get: (id, signal) => this.request("GET", `${base}/${id}`, { signal }),
            update: (id, body, signal) => this.request("PATCH", `${base}/${id}`, { body, signal }),
            deactivate: (id, signal) => this.request("DELETE", `${base}/${id}`, { signal }),
        };
    }
    get adminUsers() {
        return this.adminCrud("/api/v1/admin/users");
    }
    assignUserGroup(userId, body, signal) {
        return this.request("PUT", `/api/v1/admin/users/${userId}/groups`, { body, signal });
    }
    revokeUserGroup(userId, groupId, permissionKey, signal) {
        return this.request("DELETE", `/api/v1/admin/users/${userId}/groups/${groupId}/${permissionKey}`, { signal });
    }
    assignUserRole(userId, body, signal) {
        return this.request("PUT", `/api/v1/admin/users/${userId}/roles`, { body, signal });
    }
    revokeUserRole(userId, roleId, signal) {
        return this.request("DELETE", `/api/v1/admin/users/${userId}/roles/${roleId}`, {
            signal,
        });
    }
    getUserEffectivePermissions(userId, signal) {
        return this.request("GET", `/api/v1/admin/users/${userId}/effective-permissions`, { signal });
    }
    /** Public: is this one-time password-setup link still usable? */
    checkPasswordSetup(token, signal) {
        return this.request("GET", `/api/v1/auth/password-setup/${encodeURIComponent(token)}`, { signal });
    }
    /** Public: spend the link and set the password. */
    completePasswordSetup(token, password, signal) {
        return this.request("POST", "/api/v1/auth/password-setup", {
            body: { token, password },
            signal,
        });
    }
    /** Admin: issue a fresh setup link and mail it, superseding the previous. */
    resendSetupLink(userId, signal) {
        return this.request("POST", `/api/v1/admin/users/${userId}/setup-link`, { signal });
    }
    getUserDeletable(userId, signal) {
        return this.request("GET", `/api/v1/admin/users/${userId}/deletable`, {
            signal,
        });
    }
    /** Hard delete — 409s with the blocking tables when still referenced. */
    deleteUserPermanently(userId, signal) {
        return this.request("DELETE", `/api/v1/admin/users/${userId}/permanent`, { signal });
    }
    getUserLanguage(userId, signal) {
        return this.request("GET", `/api/v1/admin/users/${userId}/language`, {
            signal,
        });
    }
    setUserLanguage(userId, body, signal) {
        return this.request("PUT", `/api/v1/admin/users/${userId}/language`, {
            body,
            signal,
        });
    }
    get adminGroups() {
        return this.adminCrud("/api/v1/admin/groups");
    }
    listGroupUsers(groupId, signal) {
        return this.request("GET", `/api/v1/admin/groups/${groupId}/users`, { signal });
    }
    listGroupRoles(groupId, signal) {
        return this.request("GET", `/api/v1/admin/groups/${groupId}/roles`, { signal });
    }
    listGroupCustomerUsers(groupId, signal) {
        return this.request("GET", `/api/v1/admin/groups/${groupId}/customer-users`, { signal });
    }
    /** Bulk assignment counts keyed by group id (`side=users|roles`). */
    listGroupAssignmentCounts(side, signal) {
        return this.request("GET", "/api/v1/admin/groups/assignment-counts", { query: { side }, signal });
    }
    get adminRoles() {
        return this.adminCrud("/api/v1/admin/roles");
    }
    listRoleUsers(roleId, signal) {
        return this.request("GET", `/api/v1/admin/roles/${roleId}/users`, { signal });
    }
    /** Bulk assignment counts keyed by role id (`side=users|groups`). */
    listRoleAssignmentCounts(side, signal) {
        return this.request("GET", "/api/v1/admin/roles/assignment-counts", { query: { side }, signal });
    }
    /** Bulk assignment counts keyed by user id (`side=groups|roles`). */
    listUserAssignmentCounts(side, signal) {
        return this.request("GET", "/api/v1/admin/users/assignment-counts", { query: { side }, signal });
    }
    assignRoleGroup(roleId, body, signal) {
        return this.request("PUT", `/api/v1/admin/roles/${roleId}/groups`, { body, signal });
    }
    revokeRoleGroup(roleId, groupId, permissionKey, signal) {
        return this.request("DELETE", `/api/v1/admin/roles/${roleId}/groups/${groupId}/${permissionKey}`, { signal });
    }
    get adminQueues() {
        return this.adminCrud("/api/v1/admin/queues");
    }
    get adminStates() {
        return this.adminCrud("/api/v1/admin/states");
    }
    get adminPriorities() {
        return this.adminCrud("/api/v1/admin/priorities");
    }
    get adminTypes() {
        return this.adminCrud("/api/v1/admin/types");
    }
    get adminServices() {
        return this.adminCrud("/api/v1/admin/services");
    }
    get adminSlas() {
        return this.adminCrud("/api/v1/admin/slas");
    }
    get adminCustomerUsers() {
        return this.adminCrud("/api/v1/admin/customer-users");
    }
    /** Admin recovery for a lost authenticator: drops the customer's TOTP and all passkeys. */
    resetCustomerUser2fa(customerUserId, signal) {
        return this.request("POST", `/api/v1/admin/customer-users/${customerUserId}/reset-2fa`, { signal });
    }
    bulkUpdateCustomerUsers(body, signal) {
        return this.request("PATCH", "/api/v1/admin/customer-users/bulk", { body, signal });
    }
    get adminCustomerCompanies() {
        return this.adminCrud("/api/v1/admin/customer-companies");
    }
    listCustomerCompanyUsers(customerId, signal) {
        return this.request("GET", `/api/v1/admin/customer-companies/${encodeURIComponent(customerId)}/customer-users`, { signal });
    }
    assignCustomerCompany(login, customerId, signal) {
        return this.request("PUT", `/api/v1/admin/customer-users/${encodeURIComponent(login)}/companies`, { body: { customer_id: customerId }, signal });
    }
    revokeCustomerCompany(login, customerId, signal) {
        return this.request("DELETE", `/api/v1/admin/customer-users/${encodeURIComponent(login)}/companies/${encodeURIComponent(customerId)}`, { signal });
    }
    get adminSalutations() {
        return this.adminCrud("/api/v1/admin/salutations");
    }
    get adminSignatures() {
        return this.adminCrud("/api/v1/admin/signatures");
    }
    get adminTemplates() {
        return this.adminCrud("/api/v1/admin/templates");
    }
    /** Per-template edit-ACL (which groups + users may edit it). Admin only. */
    getTemplateEditors(templateId, signal) {
        return this.request("GET", `/api/v1/admin/templates/${templateId}/editors`, { signal });
    }
    setTemplateEditors(templateId, body, signal) {
        return this.request("PUT", `/api/v1/admin/templates/${templateId}/editors`, { body, signal });
    }
    /** Who may use an agent feature (agents, groups, roles). Admin only. */
    getFeatureGrants(feature, signal) {
        return this.request("GET", `/api/v1/admin/feature-grants/${feature}`, {
            signal,
        });
    }
    setFeatureGrants(feature, body, signal) {
        return this.request("PUT", `/api/v1/admin/feature-grants/${feature}`, {
            body,
            signal,
        });
    }
    /** Agent-facing template editing — only templates the caller is granted. */
    get agentTemplates() {
        const base = "/api/v1/templates";
        return {
            list: (params, signal) => this.request("GET", base, {
                query: {
                    page: params?.page,
                    page_size: params?.pageSize,
                    valid: params?.valid,
                    search: params?.search,
                    sort: params?.sort,
                    order: params?.order,
                },
                signal,
            }),
            get: (id, signal) => this.request("GET", `${base}/${id}`, { signal }),
            update: (id, body, signal) => this.request("PATCH", `${base}/${id}`, { body, signal }),
        };
    }
    listQueueTemplates(queueId, signal) {
        return this.request("GET", `/api/v1/admin/queues/${queueId}/templates`, { signal });
    }
    listTemplateQueues(templateId, signal) {
        return this.request("GET", `/api/v1/admin/templates/${templateId}/queues`, {
            signal,
        });
    }
    /** Bulk assignment counts keyed by template id (`side=queues|attachments`). */
    listTemplateAssignmentCounts(side, signal) {
        return this.request("GET", "/api/v1/admin/templates/assignment-counts", { query: { side }, signal });
    }
    /** Bulk assignment counts keyed by queue id (`side=templates|auto-responses`). */
    listQueueAssignmentCounts(side, signal) {
        return this.request("GET", "/api/v1/admin/queues/assignment-counts", { query: { side }, signal });
    }
    assignQueueTemplate(queueId, standardTemplateId, signal) {
        return this.request("PUT", `/api/v1/admin/queues/${queueId}/templates`, {
            body: { standard_template_id: standardTemplateId },
            signal,
        });
    }
    revokeQueueTemplate(queueId, standardTemplateId, signal) {
        return this.request("DELETE", `/api/v1/admin/queues/${queueId}/templates/${standardTemplateId}`, { signal });
    }
    get adminAttachments() {
        return this.adminCrud("/api/v1/admin/attachments");
    }
    listTemplateAttachments(templateId, signal) {
        return this.request("GET", `/api/v1/admin/templates/${templateId}/attachments`, { signal });
    }
    listAttachmentTemplates(attachmentId, signal) {
        return this.request("GET", `/api/v1/admin/attachments/${attachmentId}/templates`, { signal });
    }
    /** Bulk assignment counts keyed by attachment id (`side=templates`). */
    listAttachmentAssignmentCounts(side = "templates", signal) {
        return this.request("GET", "/api/v1/admin/attachments/assignment-counts", { query: { side }, signal });
    }
    /** Replace the full set of attachments linked to a standard template. */
    replaceTemplateAttachments(templateId, body, signal) {
        return this.request("PUT", `/api/v1/admin/templates/${templateId}/attachments`, {
            body,
            signal,
        });
    }
    listCustomerUserGroups(login, signal) {
        return this.request("GET", `/api/v1/admin/customer-users/${encodeURIComponent(login)}/groups`, { signal });
    }
    assignCustomerUserGroup(login, body, signal) {
        return this.request("PUT", `/api/v1/admin/customer-users/${encodeURIComponent(login)}/groups`, { body, signal });
    }
    revokeCustomerUserGroup(login, groupId, permissionKey, signal) {
        return this.request("DELETE", `/api/v1/admin/customer-users/${encodeURIComponent(login)}/groups/${groupId}/${permissionKey}`, { signal });
    }
    get adminAutoResponses() {
        return this.adminCrud("/api/v1/admin/auto-responses");
    }
    listQueueAutoResponses(queueId, signal) {
        return this.request("GET", `/api/v1/admin/queues/${queueId}/auto-responses`, { signal });
    }
    listAutoResponseQueues(autoResponseId, signal) {
        return this.request("GET", `/api/v1/admin/auto-responses/${autoResponseId}/queues`, { signal });
    }
    /** Bulk assignment counts keyed by auto-response id (`side=queues`). */
    listAutoResponseAssignmentCounts(side = "queues", signal) {
        return this.request("GET", "/api/v1/admin/auto-responses/assignment-counts", { query: { side }, signal });
    }
    assignQueueAutoResponse(queueId, autoResponseId, signal) {
        return this.request("PUT", `/api/v1/admin/queues/${queueId}/auto-responses`, {
            body: { auto_response_id: autoResponseId },
            signal,
        });
    }
    revokeQueueAutoResponse(queueId, autoResponseId, signal) {
        return this.request("DELETE", `/api/v1/admin/queues/${queueId}/auto-responses/${autoResponseId}`, { signal });
    }
    get adminDynamicFields() {
        return this.adminCrud("/api/v1/admin/dynamic-fields");
    }
    get adminWebhooks() {
        return this.adminCrud("/api/v1/admin/webhooks");
    }
    /** PGP / S-MIME key stores shared with Znuny (AdminPGP / AdminSMIME parity). */
    get adminCrypto() {
        const base = "/api/v1/admin/crypto-keys";
        const enc = encodeURIComponent;
        return {
            status: (signal) => this.request("GET", `${base}/status`, { signal }),
            audit: (signal) => this.request("GET", base, { signal }),
            pgpList: (signal) => this.request("GET", `${base}/pgp`, { signal }),
            pgpUpload: (asciiArmor, signal) => this.request("POST", `${base}/pgp`, {
                body: { ascii_armor: asciiArmor },
                signal,
            }),
            pgpDelete: (keyId, secretOnly = false, signal) => this.request("DELETE", `${base}/pgp/${enc(keyId)}`, {
                query: { secret: secretOnly },
                signal,
            }),
            /** Armored public key (text). */
            pgpExport: (keyId, signal) => this.request("GET", `${base}/pgp/${enc(keyId)}/export`, { signal }),
            smimeList: (signal) => this.request("GET", `${base}/smime`, { signal }),
            /** PEM text, or base64 of a DER certificate. */
            smimeUploadCertificate: (certificate, signal) => this.request("POST", `${base}/smime/certificates`, {
                body: { certificate },
                signal,
            }),
            smimeUploadPrivateKey: (privateKey, secret, signal) => this.request("POST", `${base}/smime/private-keys`, {
                body: { private_key: privateKey, secret },
                signal,
            }),
            smimeDelete: (filename, privateOnly = false, signal) => this.request("DELETE", `${base}/smime/${enc(filename)}`, {
                query: { private_only: privateOnly },
                signal,
            }),
            /** Certificate PEM (text). */
            smimeDownload: (filename, signal) => this.request("GET", `${base}/smime/${enc(filename)}`, { signal }),
            smimeRelations: (filename, signal) => this.request("GET", `${base}/smime/${enc(filename)}/relations`, {
                signal,
            }),
            smimeRelationAdd: (filename, caFilename, signal) => this.request("POST", `${base}/smime/${enc(filename)}/relations`, { body: { ca_filename: caFilename }, signal }),
            smimeRelationDelete: (filename, caFingerprint, signal) => this.request("DELETE", `${base}/smime/${enc(filename)}/relations`, {
                query: { ca_fingerprint: caFingerprint },
                signal,
            }),
            /** PGP / S-MIME settings: effective value + source per field (Znuny SysConfig parity). */
            settings: (signal) => this.request("GET", "/api/v1/admin/crypto-settings", { signal }),
            /** Tiqora values by field name; `null` clears (falls back to Znuny / default). */
            settingsUpdate: (values, signal) => this.request("PUT", "/api/v1/admin/crypto-settings", {
                body: { values },
                signal,
            }),
            /** Checked with a test signature before it is stored; never returned. */
            pgpPassphraseSet: (keyRef, passphrase, signal) => this.request("PUT", `/api/v1/admin/crypto-settings/pgp-passphrases/${enc(keyRef)}`, { body: { passphrase }, signal }),
            pgpPassphraseDelete: (keyRef, signal) => this.request("DELETE", `/api/v1/admin/crypto-settings/pgp-passphrases/${enc(keyRef)}`, { signal }),
            signKeyOptions: (params = {}, signal) => this.request("GET", `${base}/sign-key-options`, {
                query: { queue_id: params.queueId, email: params.email },
                signal,
            }),
        };
    }
    /** Znuny-compatible OAuth2 mail token configs (legacy oauth2_token_config). */
    get adminOAuth2TokenConfigs() {
        const base = "/api/v1/admin/oauth2-token-configs";
        const crud = this.adminCrud(base);
        return {
            ...crud,
            templates: (signal) => this.request("GET", `${base}/templates`, { signal }),
            authorizeUrl: (id, signal) => this.request("GET", `${base}/${id}/authorize-url`, { signal }),
            refresh: (id, signal) => this.request("POST", `${base}/${id}/refresh`, { signal }),
        };
    }
    /** Incoming mail accounts (legacy mail_account). */
    get adminMailAccounts() {
        return this.adminCrud("/api/v1/admin/mail-accounts");
    }
    /** Additional channel plugin config (SMS, WhatsApp, Phone, Telegram). */
    get adminChannels() {
        const base = "/api/v1/admin/channels";
        return {
            list: (signal) => this.request("GET", base, { signal }),
            get: (name, signal) => this.request("GET", `${base}/${name}`, { signal }),
            update: (name, body, signal) => this.request("PUT", `${base}/${name}`, { body, signal }),
        };
    }
    telegramWebhookRegister(body = {}, signal) {
        return this.request("POST", "/api/v1/channels/telegram/webhook-register", { body, signal });
    }
    telegramWebhookUnregister(signal) {
        return this.request("POST", "/api/v1/channels/telegram/webhook-unregister", { signal });
    }
    /**
     * API-key lifecycle. ``create`` returns the plaintext key once
     * (``ApiKeyCreated``); list/get/update never include it.
     */
    get adminApiKeys() {
        const base = "/api/v1/admin/api-keys";
        return {
            list: (params, signal) => this.request("GET", base, {
                query: {
                    page: params?.page,
                    page_size: params?.pageSize,
                    valid: params?.valid,
                },
                signal,
            }),
            get: (id, signal) => this.request("GET", `${base}/${id}`, { signal }),
            create: (body, signal) => this.request("POST", base, { body, signal }),
            update: (id, body, signal) => this.request("PATCH", `${base}/${id}`, { body, signal }),
            remove: (id, signal) => this.request("DELETE", `${base}/${id}`, { signal }),
        };
    }
    get adminQueueVariables() {
        return this.adminCrud("/api/v1/admin/queue-variables");
    }
    /** Site-specific physical columns on the Znuny ``queue`` table (read-only). */
    listQueuePhysicalVariables(queueId, signal) {
        return this.request("GET", `/api/v1/admin/queues/${queueId}/physical-variables`, { signal });
    }
    /**
     * Per-queue external customer-management-tool link (ticket-zoom header's
     * second button). At most one row per queue, so — unlike
     * {@link adminQueueVariables} — `list` returns a plain array, not a
     * paginated {@link AdminPage}.
     */
    get adminQueueCustomerLinks() {
        const base = "/api/v1/admin/queue-customer-links";
        return {
            list: (signal) => this.request("GET", base, { signal }),
            create: (body, signal) => this.request("POST", base, { body, signal }),
            update: (id, body, signal) => this.request("PATCH", `${base}/${id}`, { body, signal }),
            remove: (id, signal) => this.request("DELETE", `${base}/${id}`, { signal }),
        };
    }
    /** Resolved external customer-tool link for the ticket's queue (ticket-zoom header). */
    getTicketCustomerLink(ticketId, signal) {
        return this.request("GET", `/api/v1/tickets/${ticketId}/customer-link`, { signal });
    }
    get adminCustomerFields() {
        return this.adminCrud("/api/v1/admin/customer-fields");
    }
    listAvailableCustomerColumns(source, signal) {
        return this.request("GET", "/api/v1/admin/customer-fields/available-columns", {
            query: { source },
            signal,
        });
    }
    getMailOutbound(signal) {
        return this.request("GET", "/api/v1/admin/mail/outbound", { signal });
    }
    putMailOutbound(body, signal) {
        return this.request("PUT", "/api/v1/admin/mail/outbound", { body, signal });
    }
    getSubjectConfig(signal) {
        return this.request("GET", "/api/v1/admin/subject-config", { signal });
    }
    putSubjectConfig(body, signal) {
        return this.request("PUT", "/api/v1/admin/subject-config", {
            body,
            signal,
        });
    }
    /** "Dienste" — daemon takeover status/toggle/interval (admin). */
    getDaemons(signal) {
        return this.request("GET", "/api/v1/admin/daemons", { signal });
    }
    putDaemon(slug, body, signal) {
        return this.request("PUT", `/api/v1/admin/daemons/${slug}`, {
            body,
            signal,
        });
    }
    /** "System-Info" — installation-wide status aggregate (admin). */
    getSystemInfo(signal) {
        return this.request("GET", "/api/v1/admin/system", { signal });
    }
    /** Per-agent SSO eligibility + 2FA enforcement (admin). */
    get adminAuthConfig() {
        const base = "/api/v1/admin/auth-config";
        return {
            list: (params, signal) => this.request("GET", base, {
                query: {
                    page: params?.page,
                    page_size: params?.pageSize,
                    valid: params?.valid,
                },
                signal,
            }),
            update: (userId, body, signal) => this.request("PUT", `${base}/${userId}`, { body, signal }),
            reset2fa: (userId, signal) => this.request("POST", `${base}/${userId}/reset-2fa`, { signal }),
            getGlobal: (signal) => this.request("GET", `${base}/global`, { signal }),
            putGlobal: (body, signal) => this.request("PUT", `${base}/global`, { body, signal }),
        };
    }
    /** Browser-navigates for Kerberos/SPNEGO; not a fetch (redirect flow).
     * `next` is a same-site path to return to after a successful handshake. */
    spnegoLoginUrl(next) {
        const base = "/api/v1/auth/spnego";
        return next ? `${base}?next=${encodeURIComponent(next)}` : base;
    }
    testMailOutbound(body = {}, signal) {
        return this.request("POST", "/api/v1/admin/mail/outbound/test", {
            body,
            signal,
        });
    }
    listMailLog(params, signal) {
        return this.request("GET", "/api/v1/admin/mail/log", {
            query: {
                page: params?.page,
                page_size: params?.pageSize,
                direction: params?.direction ?? undefined,
                status: params?.status ?? undefined,
                q: params?.q ?? undefined,
                from: params?.from ?? undefined,
                to: params?.to ?? undefined,
            },
            signal,
        });
    }
    getMailLog(id, signal) {
        return this.request("GET", `/api/v1/admin/mail/log/${id}`, { signal });
    }
    // ── GDPR erasure (admin) ──────────────────────────────────────────────
    get adminGdpr() {
        const base = "/api/v1/admin/gdpr";
        return {
            preview: (body, signal) => this.request("POST", `${base}/preview`, { body, signal }),
            /** Fast match count for the live selector counter (same selector shape as `preview`). */
            selectorCount: (body, signal) => this.request("POST", `${base}/selector-count`, { body, signal }),
            /** Per-customer before/after preview (read-only). */
            recordPreview: (body, signal) => this.request("POST", `${base}/record-preview`, {
                body,
                signal,
            }),
            createJob: (body, signal) => this.request("POST", `${base}/jobs`, { body, signal }),
            listJobs: (params, signal) => this.request("GET", `${base}/jobs`, {
                query: {
                    page: params?.page,
                    page_size: params?.pageSize,
                    status: params?.status ?? undefined,
                    mode: params?.mode ?? undefined,
                    q: params?.q ?? undefined,
                    from: params?.from ?? undefined,
                    to: params?.to ?? undefined,
                },
                signal,
            }),
            getJob: (id, signal) => this.request("GET", `${base}/jobs/${id}`, { signal }),
            rollback: (id, signal) => this.request("POST", `${base}/jobs/${id}/rollback`, { signal }),
            purgeBackup: (id, signal) => this.request("POST", `${base}/jobs/${id}/purge-backup`, { signal }),
            /** Absolute URL for downloading a job's JSON backup export. */
            backupDownloadUrl: (id) => joinUrl(this.baseUrl, `${base}/jobs/${id}/backup/download`),
        };
    }
    /** @deprecated Prefer `adminGdpr.preview`. */
    previewGdprErasure(body, signal) {
        return this.adminGdpr.preview(body, signal);
    }
    /** @deprecated Prefer `adminGdpr.createJob`. */
    createGdprErasureJob(body, signal) {
        return this.adminGdpr.createJob(body, signal);
    }
    /** @deprecated Prefer `adminGdpr.listJobs`. */
    listGdprErasureJobs(params, signal) {
        return this.adminGdpr.listJobs(params, signal);
    }
    /** @deprecated Prefer `adminGdpr.getJob`. */
    getGdprErasureJob(id, signal) {
        return this.adminGdpr.getJob(id, signal);
    }
    /** @deprecated Prefer `adminGdpr.rollback`. */
    rollbackGdprErasureJob(id, signal) {
        return this.adminGdpr.rollback(id, signal);
    }
    /** @deprecated Prefer `adminGdpr.purgeBackup`. */
    purgeGdprErasureBackup(id, signal) {
        return this.adminGdpr.purgeBackup(id, signal);
    }
    /** @deprecated Prefer `adminGdpr.backupDownloadUrl`. */
    gdprErasureBackupDownloadUrl(id) {
        return this.adminGdpr.backupDownloadUrl(id);
    }
    /** Paginated admin CRUD (valid filter, edit/reactivate). */
    get adminSystemAddresses() {
        return this.adminCrud("/api/v1/admin/system-addresses");
    }
    /**
     * Valid system addresses for pickers (queues / auto-responses).
     * Thin wrapper over the paginated list — only ``valid_id = 1`` rows.
     */
    async listSystemAddresses(signal) {
        const page = await this.adminSystemAddresses.list({ valid: "valid", pageSize: 500 }, signal);
        return page.items;
    }
    createSystemAddress(body, signal) {
        return this.adminSystemAddresses.create(body, signal);
    }
    updateSystemAddress(id, body, signal) {
        return this.adminSystemAddresses.update(id, body, signal);
    }
    deleteSystemAddress(id, signal) {
        return this.adminSystemAddresses.deactivate(id, signal);
    }
    listNotificationEvents(signal) {
        return this.request("GET", "/api/v1/admin/notification-events", {
            signal,
        });
    }
    getNotificationEvent(id, signal) {
        return this.request("GET", `/api/v1/admin/notification-events/${id}`, { signal });
    }
    createNotificationEvent(body, signal) {
        return this.request("POST", "/api/v1/admin/notification-events", {
            body,
            signal,
        });
    }
    updateNotificationEvent(id, body, signal) {
        return this.request("PATCH", `/api/v1/admin/notification-events/${id}`, { body, signal });
    }
    deleteNotificationEvent(id, signal) {
        return this.request("DELETE", `/api/v1/admin/notification-events/${id}`, {
            signal,
        });
    }
    upsertGenericAgentJob(body, signal) {
        return this.request("PUT", "/api/v1/admin/generic-agent-jobs", {
            body,
            signal,
        });
    }
    updateGenericAgentJob(jobName, body, signal) {
        return this.request("PATCH", `/api/v1/admin/generic-agent-jobs/${encodeURIComponent(jobName)}`, { body, signal });
    }
    deleteGenericAgentJob(jobName, signal) {
        return this.request("DELETE", `/api/v1/admin/generic-agent-jobs/${encodeURIComponent(jobName)}`, { signal });
    }
    listTicketMentions(ticketId, signal) {
        return this.request("GET", `/api/v1/tickets/${ticketId}/mentions`, {
            signal,
        });
    }
    createTicketMention(ticketId, body, signal) {
        return this.request("POST", `/api/v1/tickets/${ticketId}/mentions`, {
            body,
            signal,
        });
    }
    deleteTicketMention(ticketId, mentionId, signal) {
        return this.request("DELETE", `/api/v1/tickets/${ticketId}/mentions/${mentionId}`, { signal });
    }
    /**
     * Cross-ticket time-accounting report (permission-scoped).
     * Filters: create_by (agent), ticket_id, created_from/to (ISO datetime).
     */
    listTimeAccountingReport(params = {}, signal) {
        return this.request("GET", "/api/v1/tickets/time-accounting", {
            query: params,
            signal,
        });
    }
    /** Printable HTML for a ticket (browser print / Save as PDF). */
    ticketPrintUrl(ticketId, params = {}) {
        const qs = new URLSearchParams();
        if (params.include_history)
            qs.set("include_history", "true");
        const suffix = qs.toString();
        return joinUrl(this.baseUrl, `/api/v1/tickets/${ticketId}/print${suffix ? `?${suffix}` : ""}`);
    }
    listTicketTimeAccounting(ticketId, signal) {
        return this.request("GET", `/api/v1/tickets/${ticketId}/time-accounting`, { signal });
    }
    createTicketTimeAccounting(ticketId, body, signal) {
        return this.request("POST", `/api/v1/tickets/${ticketId}/time-accounting`, { body, signal });
    }
    deleteTicketTimeAccounting(ticketId, entryId, signal) {
        return this.request("DELETE", `/api/v1/tickets/${ticketId}/time-accounting/${entryId}`, { signal });
    }
    listFollowUpPossible(signal) {
        return this.request("GET", "/api/v1/admin/follow-up-possible", {
            signal,
        });
    }
    // PostMaster filters (named filter = Match rows + Set rows + f_stop).
    listPostmasterFilters(signal) {
        return this.request("GET", "/api/v1/admin/postmaster-filters", {
            signal,
        });
    }
    getPostmasterFilter(filterName, signal) {
        return this.request("GET", `/api/v1/admin/postmaster-filters/${encodeURIComponent(filterName)}`, { signal });
    }
    createPostmasterFilter(body, signal) {
        return this.request("POST", "/api/v1/admin/postmaster-filters", {
            body,
            signal,
        });
    }
    updatePostmasterFilter(filterName, body, signal) {
        return this.request("PUT", `/api/v1/admin/postmaster-filters/${encodeURIComponent(filterName)}`, { body, signal });
    }
    deletePostmasterFilter(filterName, signal) {
        return this.request("DELETE", `/api/v1/admin/postmaster-filters/${encodeURIComponent(filterName)}`, { signal });
    }
    listAcls(signal) {
        return this.request("GET", "/api/v1/admin/acl", { signal });
    }
    getAcl(aclId, signal) {
        return this.request("GET", `/api/v1/admin/acl/${aclId}`, { signal });
    }
    createAcl(body, signal) {
        return this.request("POST", "/api/v1/admin/acl", { body, signal });
    }
    updateAcl(aclId, body, signal) {
        return this.request("PATCH", `/api/v1/admin/acl/${aclId}`, { body, signal });
    }
    deleteAcl(aclId, signal) {
        return this.request("DELETE", `/api/v1/admin/acl/${aclId}`, { signal });
    }
    /** ACL + attribute-relation filtered field options for new-ticket forms. */
    ticketFieldOptions(params, signal) {
        return this.request("GET", "/api/v1/reference/ticket-field-options", {
            query: {
                fields: params?.fields,
                action: params?.action,
                queue_id: params?.queueId,
                service_id: params?.serviceId,
                type_id: params?.typeId,
                state_id: params?.stateId,
                priority_id: params?.priorityId,
                sla_id: params?.slaId,
            },
            signal,
        });
    }
    /** ACL-filtered field options for an existing ticket. */
    ticketAclFieldOptions(ticketId, params, signal) {
        return this.request("GET", `/api/v1/tickets/${ticketId}/field-options`, {
            query: { fields: params?.fields, action: params?.action },
            signal,
        });
    }
    listGenericAgentJobs(signal) {
        return this.request("GET", "/api/v1/admin/generic-agent-jobs", {
            signal,
        });
    }
    getGenericAgentJob(jobName, signal) {
        return this.request("GET", `/api/v1/admin/generic-agent-jobs/${encodeURIComponent(jobName)}`, { signal });
    }
    // ── Customer portal (/api/portal) ────────────────────────────────────────
    portalLogin(body, signal) {
        return this.request("POST", "/api/portal/auth/login", {
            body,
            signal,
        });
    }
    portalMe(signal) {
        return this.request("GET", "/api/portal/auth/me", { signal });
    }
    portalLogout(signal) {
        return this.request("POST", "/api/portal/auth/logout", { signal });
    }
    /** Customer preferences: language, password, own PGP key / S-MIME certificate. */
    portalPreferences(signal) {
        return this.request("GET", "/api/portal/preferences", { signal });
    }
    portalSetLanguage(language, signal) {
        return this.request("PUT", "/api/portal/preferences/language", {
            body: { language },
            signal,
        });
    }
    portalChangePassword(currentPassword, newPassword, signal) {
        return this.request("POST", "/api/portal/preferences/password", {
            body: { current_password: currentPassword, new_password: newPassword },
            signal,
        });
    }
    portalUploadPgpKey(asciiArmor, signal) {
        return this.request("POST", "/api/portal/preferences/pgp-key", {
            body: { ascii_armor: asciiArmor },
            signal,
        });
    }
    portalUploadSmimeCertificate(certificate, signal) {
        return this.request("POST", "/api/portal/preferences/smime-certificate", { body: { certificate }, signal });
    }
    portalListTickets(params = {}, signal) {
        return this.request("GET", "/api/portal/tickets", {
            query: params,
            signal,
        });
    }
    portalCreateTicket(body, signal) {
        return this.request("POST", "/api/portal/tickets", {
            body,
            signal,
        });
    }
    portalGetTicket(ticketId, signal) {
        return this.request("GET", `/api/portal/tickets/${ticketId}`, { signal });
    }
    portalListArticles(ticketId, signal) {
        return this.request("GET", `/api/portal/tickets/${ticketId}/articles`, { signal });
    }
    portalReply(ticketId, body, signal) {
        return this.request("POST", `/api/portal/tickets/${ticketId}/reply`, {
            body,
            signal,
        });
    }
    portalUploadAttachment(ticketId, file, note = "", signal) {
        const form = new FormData();
        form.append("file", file);
        form.append("note", note);
        return this.request("POST", `/api/portal/tickets/${ticketId}/attachments`, { body: form, signal });
    }
    portalAttachmentDownloadUrl(ticketId, attachmentId) {
        return joinUrl(this.baseUrl, `/api/portal/tickets/${ticketId}/attachments/${attachmentId}`);
    }
    portalSearchKb(params, signal) {
        return this.request("GET", "/api/portal/kb/search", {
            query: params,
            signal,
        });
    }
    portalGetKbArticle(slugOrId, signal) {
        return this.request("GET", `/api/portal/kb/articles/${encodeURIComponent(slugOrId)}`, { signal });
    }
    // ── Stats (/api/v1/stats) ────────────────────────────────────────────────
    statsVolume(params = {}, signal) {
        return this.request("GET", "/api/v1/stats/volume", {
            query: params,
            signal,
        });
    }
    statsVolumeCsvUrl(params = {}) {
        return this.buildStatsCsvUrl("/api/v1/stats/volume.csv", params);
    }
    statsOpenSnapshot(params = {}, signal) {
        return this.request("GET", "/api/v1/stats/open-snapshot", {
            query: params,
            signal,
        });
    }
    statsOpenSnapshotCsvUrl(params = {}) {
        return this.buildStatsCsvUrl("/api/v1/stats/open-snapshot.csv", params);
    }
    statsSla(params = {}, signal) {
        return this.request("GET", "/api/v1/stats/sla", {
            query: params,
            signal,
        });
    }
    statsSlaCsvUrl(params = {}) {
        return this.buildStatsCsvUrl("/api/v1/stats/sla.csv", params);
    }
    statsAgentWorkload(params = {}, signal) {
        return this.request("GET", "/api/v1/stats/agent-workload", {
            query: params,
            signal,
        });
    }
    statsAgentWorkloadCsvUrl(params = {}) {
        return this.buildStatsCsvUrl("/api/v1/stats/agent-workload.csv", params);
    }
    statsBacklog(params = {}, signal) {
        return this.request("GET", "/api/v1/stats/backlog", {
            query: params,
            signal,
        });
    }
    statsBacklogCsvUrl(params = {}) {
        return this.buildStatsCsvUrl("/api/v1/stats/backlog.csv", params);
    }
    /**
     * Build a CSV export URL for a stats report. Consumed via a plain
     * navigation/anchor (cookie-authenticated download), not a fetch call —
     * mirrors {@link ApiClient.exportTicketsCsvUrl}.
     */
    buildStatsCsvUrl(path, params) {
        const qs = new URLSearchParams();
        for (const [k, v] of Object.entries(params)) {
            if (v === undefined || v === null || v === "")
                continue;
            qs.set(k, String(v));
        }
        const suffix = qs.toString();
        return joinUrl(this.baseUrl, `${path}${suffix ? `?${suffix}` : ""}`);
    }
    // ── Calendar (/api/v1/calendar) ──────────────────────────────────────────
    listCalendars(signal) {
        return this.request("GET", "/api/v1/calendar/calendars", { signal });
    }
    listAppointments(params, signal) {
        const qs = new URLSearchParams();
        qs.set("start", params.start);
        qs.set("end", params.end);
        for (const id of params.calendar_id ?? [])
            qs.append("calendar_id", String(id));
        return this.request("GET", `/api/v1/calendar/appointments?${qs.toString()}`, { signal });
    }
    createAppointment(body, signal) {
        return this.request("POST", "/api/v1/calendar/appointments", {
            body,
            signal,
        });
    }
    getAppointment(appointmentId, signal) {
        return this.request("GET", `/api/v1/calendar/appointments/${appointmentId}`, { signal });
    }
    updateAppointment(appointmentId, body, signal) {
        return this.request("PATCH", `/api/v1/calendar/appointments/${appointmentId}`, { body, signal });
    }
    deleteAppointment(appointmentId, params = {}, signal) {
        return this.request("DELETE", `/api/v1/calendar/appointments/${appointmentId}`, {
            query: params,
            signal,
        });
    }
    linkAppointmentTicket(appointmentId, ticketId, signal) {
        return this.request("POST", `/api/v1/calendar/appointments/${appointmentId}/tickets/${ticketId}`, { signal });
    }
    unlinkAppointmentTicket(appointmentId, ticketId, signal) {
        return this.request("DELETE", `/api/v1/calendar/appointments/${appointmentId}/tickets/${ticketId}`, { signal });
    }
    listAppointmentTicketLinks(appointmentId, signal) {
        return this.request("GET", `/api/v1/calendar/appointments/${appointmentId}/tickets`, { signal });
    }
    calendarExportIcsUrl(calendarId) {
        return joinUrl(this.baseUrl, `/api/v1/calendar/calendars/${calendarId}/export.ics`);
    }
    // ── Admin Ticket Attribute Relations ───────────────────────────────────
    listTicketAttributeRelations(signal) {
        return this.request("GET", "/api/v1/admin/ticket-attribute-relations", { signal });
    }
    getTicketAttributeRelation(relationId, signal) {
        return this.request("GET", `/api/v1/admin/ticket-attribute-relations/${relationId}`, { signal });
    }
    createTicketAttributeRelation(body, signal) {
        return this.request("POST", "/api/v1/admin/ticket-attribute-relations", { body, signal });
    }
    updateTicketAttributeRelation(relationId, body, signal) {
        return this.request("PATCH", `/api/v1/admin/ticket-attribute-relations/${relationId}`, { body, signal });
    }
    deleteTicketAttributeRelation(relationId, signal) {
        return this.request("DELETE", `/api/v1/admin/ticket-attribute-relations/${relationId}`, {
            signal,
        });
    }
}
